/**
 * Parse-guided rescues — the two strategies that let the GRAMMAR judge an
 * almost-failed clause instead of a heuristic (D18 near-tie resolution,
 * 2026-08-13; D21 verb demotion, 2026-08-13).
 *
 * @remarks
 * Both rescues share one principle: when the straight walk stalls, candidate
 * readings run the REAL remaining pipeline, and only a reading the
 * downstream stages vouch for may win. The orchestrator (`parse-command.ts`)
 * supplies its walk stages as hooks, so this module never re-implements —
 * or drifts from — scoring, resolution, or assembly.
 *
 * **D18 — near-tie resolution.** A fuzzy tie between candidate corrections
 * ("gaet" → `get` | `gate`) substitutes each target into the raw stream and
 * re-parses; a UNIQUELY viable reading wins, carried as an ordinary
 * correction. Genuine ambiguity falls back to the §6.2 policy
 * ({@link nearTieResult}).
 *
 * **D21 — verb demotion.** A mid-clause intent token ("take the pry bar"
 * where `pry` aliases `use`) demotes to its SURFACE word and the clause
 * re-walks; the demoted reading wins only as a command or a which-one
 * clarification. Anything less keeps the original second-verb teach, so
 * confused input ("take get") still teaches honestly. Clause-initial verbs
 * are untouched, and termination needs no cap: each pass removes one intent
 * token and `second_verb` requires one to remain.
 *
 * **D216 — a verb word in the modifier slot is an adjective, even when
 * nothing answers to it.** English reads a word by where it stands: in
 * `open jar` the first word is the verb, and in `examine open jars` the same
 * word describes the jars. D21 already demoted it, but let the catalog vote
 * only for a command or a question — so when no open jar was in reach, the
 * demoted reading's honest answer ("you see no open jar here") was thrown
 * away and the player was told the VERB could not carry the word. Mike's
 * ruling (2026-09-28) is that this is grammar, never a catalog word bolted
 * onto the jars. So an entity miss now also wins, but only for a demoted word
 * standing where a modifier stands: inside a phrase, before its last word,
 * with no word the catalog names a thing by in front of it. `take lamp open
 * door` keeps its second-verb teach, because `lamp` had already closed the
 * phrase when `open` arrived — that is a run-on, not a description.
 *
 * **D221 — and a verb word at the END of a described phrase is its noun.**
 * `examine the burnt place` in a room where no place is named answered
 * `INSPECT could not carry "place"`: `place` is a verb, it stood last, and
 * D216 only let a word BEFORE the last one describe. But a word that
 * something describes is a noun by the same grammar (`the burnt place`, `a
 * brass lock`), so a demoted verb word ending a phrase an article opened, with
 * words before it and no thing's name among them, now yields the miss as well:
 * `You see no burnt place here.` Without the article it keeps the teach
 * (`examine clay open` reads as a run-on, D216's pin), alone it is still a
 * verb (`examine open`), and after a thing's name it is still a run-on (`take
 * lamp open`).
 *
 * @packageDocumentation
 */

// cspell:ignore gaet -- the D18 remarks cite the motivating player typo

import { type FuzzyCandidatePool, type NearTie } from '../fuzzy/correct-clause-tokens.js';
import { type CanonicalToken } from '../grammar/canonicalize-tokens.js';
import {
	type ClauseStructure,
	matchClauseStructure,
	type PhraseToken,
	type StructureDiagnostic,
	type StructureVocabulary,
} from '../grammar/match-clause-structure.js';
import { CLARIFICATION_CANDIDATE_CAP } from '../rank/select-or-clarify.js';
import {
	type ClarificationRequest,
	type EntityRef,
	type ParseCandidateEntity,
	type ParsedCommand,
	type ParseDiagnostics,
	type Token,
	type TokenCorrection,
} from '../types.js';

/** One independently-parseable clause: its folded text and span-bearing tokens. */
export interface ClauseSegment {
	readonly clauseText: string;
	readonly tokens: readonly Token[];
}

/** How one clause's walk through the pipeline ended (the orchestrator maps it to `ParseOutcome`). */
export type ClauseParseResult =
	| { readonly clarification: ClarificationRequest; readonly type: 'clarification' }
	| { readonly command: ParsedCommand; readonly type: 'command' }
	| { readonly diagnostics: ParseDiagnostics; readonly type: 'diagnostics' };

/** The orchestrator's resolve → rank → assemble tail, injected so rescues cannot drift from it. */
export type CompleteClauseWalkHook = (structure: ClauseStructure) => ClauseParseResult;

/** The orchestrator's full clause walk, injected for D18's candidate re-parses. */
export type ParseClauseHook = (
	clauseSegment: ClauseSegment,
	seedCorrections: readonly TokenCorrection[],
	tieResolutionDepth: number,
) => ClauseParseResult;

/**
 * D21: rescue a `second_verb` failure by demoting the offending mid-clause
 * intent token to its surface word and re-walking the clause (see the module
 * remarks).
 *
 * @param diagnostic - The structure failure that stopped the straight walk
 * @param canonicalTokens - The clause's canonical tokens as they failed
 * @param vocabulary - The structure matcher's vocabulary view
 * @param completeWalk - The orchestrator's walk tail (resolve → rank → assemble)
 * @param headWords - D216: every word the catalog names a thing by, which is what closes a noun phrase
 * @returns The rescued result, or `undefined` when the original diagnostic should stand
 */
export const demoteMidClauseVerbsAndReparse = (
	diagnostic: StructureDiagnostic,
	canonicalTokens: readonly CanonicalToken[],
	vocabulary: StructureVocabulary,
	completeWalk: CompleteClauseWalkHook,
	headWords: ReadonlySet<string>,
): ClauseParseResult | undefined => {
	let workingTokens = canonicalTokens;
	let workingDiagnostic = diagnostic;
	const demotedSpanStarts: number[] = [];

	while (workingDiagnostic.kind === 'structure_not_understood' && workingDiagnostic.reason === 'second_verb') {
		const offendingSpanStart = workingDiagnostic.offendingToken.sourceSpan.start;

		demotedSpanStarts.push(offendingSpanStart);

		workingTokens = workingTokens.map((canonicalToken) =>
			canonicalToken.kind === 'intent' && canonicalToken.sourceSpan.start === offendingSpanStart
				? { kind: 'word', sourceSpan: canonicalToken.sourceSpan, sourceText: canonicalToken.sourceText }
				: canonicalToken,
		);

		const retryOutcome = matchClauseStructure(workingTokens, vocabulary);

		if (retryOutcome.type === 'unmatched') {
			// Another offender (or a different failure): the loop condition judges
			// the new diagnostic — a second collision demotes again.
			workingDiagnostic = retryOutcome.diagnostic;
			continue;
		}

		const retryResult = completeWalk(retryOutcome.structure);

		// The catalog is the judge: rescue only what it vouches for — a command,
		// or a which-one question. D216/D221: or an entity miss, when every word
		// the walk demoted stood inside a noun phrase, describing or described,
		// because there the grammar had already decided the word was not a verb
		// (see the module remarks). Any other diagnostic keeps the original teach.
		if (retryResult.type !== 'diagnostics') {
			return retryResult;
		}

		return retryResult.diagnostics.failureKind === 'entity_not_found' &&
			demotedSpanStarts.every((spanStart) => standsInsideANounPhrase(retryOutcome.structure, spanStart, headWords))
			? retryResult
			: undefined;
	}

	// A non-second-verb failure (originally, or after demotion): the original
	// diagnostic stands.
	return undefined;
};

/**
 * D216 and D221: does the word at this span stand INSIDE a noun phrase —
 * describing its head, or being its head with a description in front of it —
 * with nothing before it that names a thing?
 *
 * @remarks
 * Before the last word it is a modifier (`examine open jars`, D216). As the
 * last word it is the noun the words before it describe, but only in a phrase
 * an ARTICLE opened (`examine the burnt place`, D221): the article is English
 * saying a noun phrase follows, and a noun phrase ends in its noun. Without
 * one, a verb word at the end is still read as the run-on it usually is
 * (`examine clay open`), and alone it is neither (`examine open` has no jar in
 * it). A word the catalog names a thing by closes the phrase: in `take lamp
 * open door`, the lamp was a whole object before `open` arrived, so `open`
 * begins a second clause the player ran on, and the teach that names the
 * second verb is the true one. And an article OPENS one: in `take take the
 * keycard` the phrase starts at `the`, so the second `take` stands outside it,
 * in front, where only a verb stands — nothing describes a noun from the far
 * side of its article.
 *
 * @param structure - The clause as it matched once the word was demoted
 * @param spanStart - Where the demoted word began in the input
 * @param headWords - Every word the catalog names a thing by
 * @returns Whether the word belongs to the phrase it sits in
 */
const standsInsideANounPhrase = (structure: ClauseStructure, spanStart: number, headWords: ReadonlySet<string>): boolean =>
	phrasesOf(structure).some((phrase) => {
		const wordIndex = phrase.findIndex((phraseToken) => phraseToken.sourceSpan.start === spanStart);

		if (wordIndex === -1) {
			return false;
		}

		const isDescribingTheHead = wordIndex < phrase.length - 1;
		const isTheDescribedHead = wordIndex > 0 && wordIndex === phrase.length - 1 && phrase[0] !== undefined && 'isNounPhraseStart' in phrase[0];

		return (
			(isDescribingTheHead || isTheDescribedHead) &&
			phrase.slice(0, wordIndex).every((earlierToken) => !headWords.has(earlierToken.sourceText)) &&
			phrase.slice(wordIndex + 1).every((laterToken) => !('isNounPhraseStart' in laterToken))
		);
	});

/**
 * The noun phrases a matched clause carries — its object, its bound phrase,
 * both, or neither.
 *
 * @param structure - A matched clause
 * @returns Its phrases, object first
 */
const phrasesOf = (structure: ClauseStructure): readonly (readonly PhraseToken[])[] => [
	...('objectPhrase' in structure ? [structure.objectPhrase] : []),
	...('boundPhrase' in structure ? [structure.boundPhrase] : []),
];

/**
 * D216: every word the catalog names a thing by — the last word of each
 * noun and alias, which is where a name's head sits (`blast door` is a door).
 * These are the words that close a noun phrase for
 * {@link demoteMidClauseVerbsAndReparse}.
 *
 * @param entities - The parse catalog
 * @returns The head words, folded as the resolver folds names
 */
export const catalogHeadWordsOf = (entities: readonly ParseCandidateEntity[]): ReadonlySet<string> =>
	new Set(
		entities.flatMap((entity) =>
			[...entity.nouns, ...entity.aliases].map((name) => name.trim().toLowerCase().replace(/^.*\s/u, '')),
		),
	);

/**
 * D18: resolve a near-tie by letting the grammar judge. Each tied target is
 * substituted into the raw stream and walked through the full remaining
 * pipeline; a reading that UNIQUELY parses (or, failing any parse, uniquely
 * reaches a clarification) wins, carrying the substitution as an ordinary
 * correction. Several viable readings — or none — mean the tie was genuine,
 * and the §6.2 fallback stands unchanged.
 *
 * Deterministic throughout: candidates are tried in the tie's pool-sorted
 * target order, and "unique" is judged on parsed MEANING (intent, direction,
 * refs), so two spellings of the same reading — e.g. tied targets that are
 * aliases of one entity — still resolve rather than falsely tying.
 *
 * @param tie - The leftmost near-tie the fuzzy pass hit
 * @param clauseSegment - The clause whose walk hit it
 * @param fuzzyPool - The parse call's shared correction pool
 * @param parseClause - The orchestrator's full clause walk (candidate walks recurse through it)
 * @param seedCorrections - Corrections already patched into this clause by outer tie resolutions
 * @param tieResolutionDepth - The CURRENT walk's remaining depth (candidate walks receive one less)
 * @returns The winning reading, or the §6.2 fallback
 */
export const resolveNearTieByParsing = (
	tie: NearTie,
	clauseSegment: ClauseSegment,
	fuzzyPool: FuzzyCandidatePool,
	parseClause: ParseClauseHook,
	seedCorrections: readonly TokenCorrection[],
	tieResolutionDepth: number,
): ClauseParseResult => {
	const commandResults: { readonly meaningKey: string; readonly result: ClauseParseResult }[] = [];
	const clarificationResults: ClauseParseResult[] = [];

	for (const targetWord of tie.targetWords) {
		const patchedTokens = clauseSegment.tokens.map((rawToken) =>
			rawToken.sourceSpan.start === tie.sourceSpan.start
				? { sourceSpan: rawToken.sourceSpan, text: targetWord }
				: rawToken,
		);
		const candidateResult = parseClause(
			{ clauseText: clauseSegment.clauseText, tokens: patchedTokens },
			[
				...seedCorrections,
				{
					correctedText: targetWord,
					editDistance: tie.editDistance,
					originalText: tie.originalText,
					sourceSpan: tie.sourceSpan,
				},
			],
			tieResolutionDepth - 1,
		);

		if (candidateResult.type === 'command') {
			commandResults.push({ meaningKey: commandMeaningKey(candidateResult.command), result: candidateResult });
		} else if (candidateResult.type === 'clarification') {
			clarificationResults.push(candidateResult);
		}
	}

	const distinctMeaningKeys = new Set(commandResults.map((commandResult) => commandResult.meaningKey));

	// Exactly one reading parses — or several spellings of the SAME reading do
	// (first in pool order wins the echo): the grammar has judged.
	if (distinctMeaningKeys.size === 1 && commandResults[0] !== undefined) {
		return commandResults[0].result;
	}

	// No reading parses outright, but exactly one reaches a clarification: a
	// question beats a rejection.
	if (commandResults.length === 0 && clarificationResults.length === 1 && clarificationResults[0] !== undefined) {
		return clarificationResults[0];
	}

	return nearTieResult(tie, fuzzyPool, clauseSegment.clauseText);
};

/**
 * Map a near-tie onto its §6.2 outcome: a clarification when every tied
 * target is a catalog word (the player almost-named an entity — ask which),
 * otherwise `unrecognized` with the tied targets as suggestions.
 *
 * @param tie - The leftmost near-tie the fuzzy pass hit
 * @param fuzzyPool - The parse call's pool (owns the word → entities view)
 * @param clauseText - The clause, folded (clarifications cite it)
 * @returns The clause result
 */
export const nearTieResult = (tie: NearTie, fuzzyPool: FuzzyCandidatePool, clauseText: string): ClauseParseResult => {
	const entityMatches = new Map<EntityRef, { entity: ParseCandidateEntity; matchedWords: string[] }>();

	let isEveryTargetCatalogWord = true;

	for (const targetWord of tie.targetWords) {
		const owningEntities = fuzzyPool.entitiesByWord.get(targetWord);

		if (owningEntities === undefined) {
			isEveryTargetCatalogWord = false;
			continue;
		}

		for (const owningEntity of owningEntities) {
			const entityMatch = entityMatches.get(owningEntity.entityRef) ?? { entity: owningEntity, matchedWords: []};

			entityMatch.matchedWords.push(targetWord);
			entityMatches.set(owningEntity.entityRef, entityMatch);
		}
	}

	if (!isEveryTargetCatalogWord) {
		return {
			diagnostics: {
				failureKind: 'no_intent_recognized',
				nearMissSuggestions: tie.targetWords,
				unmatchedTokens: [tie.originalText],
			},
			type: 'diagnostics',
		};
	}

	const rankedCandidates = [...entityMatches.values()]
		.sort(
			(leftMatch, rightMatch) =>
				rightMatch.entity.salience - leftMatch.entity.salience ||
				leftMatch.entity.entityRef.localeCompare(rightMatch.entity.entityRef, 'en'),
		)
		.slice(0, CLARIFICATION_CANDIDATE_CAP)
		.map((entityMatch) => ({ entityRef: entityMatch.entity.entityRef, matchedTokens: entityMatch.matchedWords }));

	return {
		clarification: { candidates: rankedCandidates, clauseText, kind: 'which_entity' },
		type: 'clarification',
	};
};

/**
 * A parsed command's MEANING, as a stable key (D18 uniqueness judgment):
 * intent, direction, and the three resolvable refs — never the spelling that
 * produced them.
 *
 * @param command - The parsed command
 * @returns The deterministic meaning key
 */
const commandMeaningKey = (command: ParsedCommand): string =>
	[
		command.intent,
		command.direction ?? '',
		command.directObjectRef ?? '',
		command.indirectObjectRef ?? '',
		command.toolRef ?? '',
	].join(' ');
